import { type FC, useEffect, useState } from 'react';
import { ReactComponent as ClockIcon } from 'assets/icons/button-icons/clock.svg';

import { useCart } from 'components/logic/Cart/hooks/useCart';
import { gtmClickAddToCartTrialMiner } from 'utils/gtmSender/gtmSender';

import { ButtonsContainer } from './components/ui';

import * as S from './AddToCartButton.styled';

interface IAddToCartButtonProps {
  isSoldOut: boolean;
  isTrial?: boolean;
  disabled?: boolean;
  id: number;
  t: (key: string) => string;
}

export const AddToCartButton: FC<IAddToCartButtonProps> = ({
  isSoldOut,
  id,
  isTrial,
  disabled,
  t,
}) => {
  const [isMouseOnButton, setIsMouseOnButton] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const {
    cartItemsQuantityMap,
    handleIncrement,
    handleDecrement,
    handleItemInCart,
    handleInputChange,
    handleKeyDown,
    handleBlur,
  } = useCart();

  const itemInCart = handleItemInCart(id);

  const handleMouseEnter = () => {
    setIsMouseOnButton(true);
  };

  const handleMouseLeave = () => {
    setIsMouseOnButton(false);
  };

  const handleInputFocused = (value: boolean) => setIsInputFocused(value);

  const currentMiner = cartItemsQuantityMap.get(id);

  useEffect(() => {
    if (!itemInCart && !isMouseOnButton) {
      setIsMouseOnButton(false);
    }
  }, [itemInCart, isMouseOnButton]);

  useEffect(() => {
    return () => {
      handleMouseLeave();
      handleInputFocused(false);
    };
  }, []);

  if (isSoldOut) {
    return (
      <S.AddToCartButtonsWrapper>
        <S.OutOfStockBadge>
          <ClockIcon />
          {t('purchase.outOfStock')}
        </S.OutOfStockBadge>
      </S.AddToCartButtonsWrapper>
    );
  }

  if (
    (!currentMiner || currentMiner?.currentQuantity === 0) &&
    !isMouseOnButton &&
    !isInputFocused
  ) {
    return (
      <S.AddToCartButtonsWrapper>
        <S.AddToCartButton
          type="button"
          icon={<ClockIcon />}
          text={t('purchase.addToCart')}
          onClick={() => {
            isTrial && gtmClickAddToCartTrialMiner();
            handleMouseEnter();
            handleIncrement(id);
          }}
          disabled={disabled}
        />
      </S.AddToCartButtonsWrapper>
    );
  }

  return (
    <S.AddToCartButtonsWrapper onMouseLeave={handleMouseLeave}>
      <ButtonsContainer
        id={id}
        inputValue={currentMiner?.currentQuantity ?? 0}
        isIncrementDisabled={
          (currentMiner && currentMiner?.currentQuantity >= currentMiner?.maxQuantity) ||
          (currentMiner && currentMiner?.currentQuantity >= 1 && isTrial) ||
          false
        }
        isInputFocused={isInputFocused}
        handleIncrement={handleIncrement}
        handleDecrement={handleDecrement}
        handleInputChange={handleInputChange}
        handleMouseLeave={handleMouseLeave}
        handleMouseEnter={handleMouseEnter}
        handleInputFocused={handleInputFocused}
        handleBlur={handleBlur}
        handleKeyDown={handleKeyDown}
      />
    </S.AddToCartButtonsWrapper>
  );
};
